import * as Common from "../../commons/common"
import http from "../../plugins/http"

const MasterExcelWorker = require("worker-loader!../../workers/masterExcelWorker").default;

export type MasterExcelDownloadConfig = {
  apiPath: string
  condition: any
  fileName: string
  confirmMessage: string
  columns: string[]
  deleteColumnCount: number
}

const downloadRowCount = 1000;

export const downloadMasterExcel = async (context: any, config: MasterExcelDownloadConfig): Promise<void> => {
  if(!window.confirm(config.confirmMessage)){
    return;
  }

  context.root.$store.dispatch("showOverlay", {
    overlayText: "ダウンロードデータ取得中... しばらくお待ちください。",
    isShowProgress: true,
  });

  let worker: Worker = null;

  try {
    if(typeof Worker === "undefined"){
      alert("この環境ではExcelダウンロードを実行できません。別のブラウザでお試しください。");
      return;
    }

    worker = new MasterExcelWorker();
    const templateResponse = await http.get(`/api/teacher/${config.apiPath}/download_template`, {responseType: 'arraybuffer'});
    const templateBuffer = templateResponse.data as ArrayBuffer;
    if(!(templateBuffer instanceof ArrayBuffer)){
      throw new Error("Excelテンプレートを取得できませんでした。");
    }

    await postWorkerMessage(worker, {
      type: "init",
      templateBuffer: templateBuffer,
      columns: config.columns,
      deleteColumnCount: config.deleteColumnCount,
    }, "initialized", [templateBuffer]);

    const countResponse = await http.get(`/api/teacher/${config.apiPath}/download_count?${Common.BuildQuery(config.condition, 'search_condition')}`);
    const fullCount = Number(countResponse.data.full_count);
    if(fullCount == 0){
      alert("条件に一致するデータはありませんでした。");
      return;
    }

    let downloadedCount = 0;
    const pageCount = Math.ceil(fullCount / downloadRowCount);

    for(let page = 1; page <= pageCount; page++){
      const downloadCondition = Object.assign({}, config.condition, {
        page: page,
        row_count: downloadRowCount,
      });
      const response = await http.get(`/api/teacher/${config.apiPath}/download_data?${Common.BuildQuery(downloadCondition, 'search_condition')}`);
      const rows = response.data.rows;

      if(rows.length == 0){
        break;
      }

      await postWorkerMessage(worker, {
        type: "appendRows",
        rows: rows,
      }, "appended");

      downloadedCount += rows.length;
      const progress = Math.min(90, Math.floor((downloadedCount / fullCount) * 900) / 10);
      context.root.$store.commit("setProgressText", progress.toFixed(1));
      context.root.$store.commit("setOverlayText", `ダウンロードデータ取得中... ${downloadedCount}/${fullCount}件`);
    }

    context.root.$store.commit("setProgressText", "95.0");
    context.root.$store.commit("setOverlayText", "Excelファイル作成中... しばらくお待ちください。");
    const workerResponse = await postWorkerMessage(worker, { type: "finish" }, "finished");

    const blob = new Blob([workerResponse.buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const a = document.createElement('a');
    const objectUrl = window.URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = config.fileName;
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);

    context.root.$store.commit("setProgressText", "100.0");
  }
  catch(error){
    if(error.response && error.response.status == 422){
      alert(`エクセルのダウンロードに失敗しました。\n${getErrorMessage(error)}`);
    }else if(error.message){
      alert(`エクセルのダウンロードに失敗しました。\n${error.message}`);
    }else{
      alert("エクセルのダウンロードに失敗しました。");
    }
  }
  finally {
    if(worker){
      worker.terminate();
    }
    context.root.$store.dispatch("hideOverlay");
  }
}

const postWorkerMessage = (worker: Worker, message: any, expectedType: string, transfer?: Transferable[]): Promise<any> => {
  return new Promise((resolve, reject) => {
    const onMessage = (event: MessageEvent) => {
      if(event.data.type === "error"){
        cleanup();
        reject(new Error(event.data.message));
        return;
      }
      if(event.data.type === expectedType){
        cleanup();
        resolve(event.data);
      }
    };
    const onError = (error: ErrorEvent) => {
      cleanup();
      reject(error);
    };
    const cleanup = () => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);
    worker.postMessage(message, transfer || []);
  });
}

const getErrorMessage = (error: any): string => {
  try {
    const responseData = error.response.data;
    if(responseData instanceof ArrayBuffer){
      const errorData = JSON.parse((new TextDecoder).decode(new Uint8Array(responseData)));
      return errorData.errors[0];
    }
    return responseData.errors[0];
  }
  catch(e){
    return "入力内容を確認してください。";
  }
}
